import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig';

const AdminUtilities = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/auth/users', {
        params: { limit: 100 }
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error loading users:', error);
      alert('Error loading users: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const lockUser = async (email) => {
    if (!confirm(`Are you sure you want to LOCK the account for ${email}?`)) return;
    
    try {
      await axiosInstance.put('/auth/lock-account', { email });
      alert(`✅ Account successfully locked for ${email}`);
      fetchUsers(); // Refresh the list
    } catch (error) {
      alert(`❌ Error locking account: ${error.response?.data?.error || error.message}`);
    }
  };

  const unlockUser = async (email) => {
    if (!confirm(`Are you sure you want to UNLOCK the account for ${email}?`)) return;
    
    try {
      await axiosInstance.post('/auth/unlock-account', { email });
      alert(`✅ Account successfully unlocked for ${email}`);
      fetchUsers(); // Refresh the list
    } catch (error) {
      alert(`❌ Error unlocking account: ${error.response?.data?.error || error.message}`);
    }
  };

  const filteredUsers = users.filter(user => 
    user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const lockedUsers = filteredUsers.filter(user => 
    user.lockUntil && new Date(user.lockUntil) > new Date()
  );

  const activeUsers = filteredUsers.filter(user => 
    !user.lockUntil || new Date(user.lockUntil) <= new Date()
  );

  useEffect(() => {
    fetchUsers();
  }, []);

  return (
    <div style={{ 
      maxWidth: '1200px', 
      margin: '0 auto', 
      padding: '20px',
      backgroundColor: '#f9fafb',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '12px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h1 style={{
          fontSize: '28px',
          fontWeight: 'bold',
          color: '#dc2626',
          margin: '0 0 8px 0'
        }}>
          🔧 Admin Utilities - User Account Management
        </h1>
        <p style={{ color: '#6b7280', margin: 0 }}>
          Lock and unlock user accounts as needed
        </p>
      </div>

      {/* Controls */}
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        display: 'flex',
        gap: '16px',
        alignItems: 'center'
      }}>
        <input
          type="text"
          placeholder="Search users by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: 1,
            padding: '12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px'
          }}
        />
        <button
          onClick={fetchUsers}
          disabled={loading}
          style={{
            backgroundColor: '#3b82f6',
            color: 'white',
            padding: '12px 24px',
            border: 'none',
            borderRadius: '6px',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1
          }}
        >
          {loading ? 'Loading...' : `Refresh (${users.length} users)`}
        </button>
      </div>

      {/* Summary Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3b82f6' }}>
            {users.length}
          </div>
          <div style={{ color: '#6b7280', fontSize: '14px' }}>Total Users</div>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#10b981' }}>
            {activeUsers.length}
          </div>
          <div style={{ color: '#6b7280', fontSize: '14px' }}>Active Users</div>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#ef4444' }}>
            {lockedUsers.length}
          </div>
          <div style={{ color: '#6b7280', fontSize: '14px' }}>Locked Users</div>
        </div>
      </div>

      {/* Locked Users Section */}
      {lockedUsers.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#fef2f2',
            borderRadius: '12px 12px 0 0'
          }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#dc2626',
              margin: 0
            }}>
              🔒 Locked Accounts ({lockedUsers.length})
            </h2>
          </div>
          <div style={{ padding: '16px' }}>
            {lockedUsers.map(user => (
              <div key={user._id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px',
                marginBottom: '12px',
                backgroundColor: '#fef2f2',
                borderRadius: '8px',
                border: '1px solid #fecaca'
              }}>
                <div>
                  <div style={{ fontWeight: '600', color: '#111827' }}>
                    {user.firstName} {user.lastName}
                  </div>
                  <div style={{ color: '#6b7280', fontSize: '14px' }}>
                    {user.email}
                  </div>
                  <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                    Role: {user.role} • Locked until: {new Date(user.lockUntil).toLocaleString()}
                  </div>
                </div>
                <button
                  onClick={() => unlockUser(user.email)}
                  style={{
                    backgroundColor: '#10b981',
                    color: 'white',
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  🔓 UNLOCK
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active Users Section */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#f0fdf4',
          borderRadius: '12px 12px 0 0'
        }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#059669',
            margin: 0
          }}>
            ✅ Active Accounts ({activeUsers.length})
          </h2>
        </div>
        <div style={{ padding: '16px' }}>
          {activeUsers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              No active users found matching your search.
            </div>
          ) : (
            activeUsers.map(user => (
              <div key={user._id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px',
                marginBottom: '12px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <div>
                  <div style={{ fontWeight: '600', color: '#111827' }}>
                    {user.firstName} {user.lastName}
                  </div>
                  <div style={{ color: '#6b7280', fontSize: '14px' }}>
                    {user.email}
                  </div>
                  <div style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500',
                    marginTop: '4px',
                    backgroundColor: user.role === 'admin' ? '#fef2f2' : '#eff6ff',
                    color: user.role === 'admin' ? '#dc2626' : '#2563eb'
                  }}>
                    {user.role}
                  </div>
                </div>
                <button
                  onClick={() => lockUser(user.email)}
                  style={{
                    backgroundColor: '#ef4444',
                    color: 'white',
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  🔒 LOCK
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUtilities;